"use client";

import { useMemo, useState, useTransition } from "react";
import { Check, Clock, Loader2, MapPin } from "lucide-react";

import { Alert, AlertDescription } from "@/template/ui-kit/alert";
import { Button } from "@/template/ui-kit/button";
import { Calendar } from "@/template/ui-kit/calendar";
import { Input } from "@/template/ui-kit/input";
import { Label } from "@/template/ui-kit/label";
import { Textarea } from "@/template/ui-kit/textarea";
import { WhatsappButton } from "@/template/ui-kit/whatsapp-button";

import {
  getBookableDaysAction,
  getSlotsAction,
  submitBookingAction,
  type BookingFormState,
} from "@/app/p/[slug]/booking-action";

type SessionType = { id: string; name: string; duration: number };

type PublicAgendaOption = {
  id: string;
  name: string;
  /** Vacío = ofrece todos los servicios. */
  sessionTypeIds: string[];
};

type Props = {
  slug: string;
  displayName: string;
  specialty: string | null;
  practiceAddress: string | null;
  practicePhone: string | null;
  autoConfirm: boolean;
  sessionTypes: SessionType[];
  /**
   * Multi-agenda: salas / personas entre las que el cliente elige (o deja
   * "cualquiera"). Vacío = cuenta de una sola agenda, el paso no aparece.
   */
  agendaLabel?: string;
  agendas?: PublicAgendaOption[];
  initialMonth: string;
  initialBookableDays: string[];
  /** Página v2: servicio / agenda ya elegidos desde un link o un botón. */
  initialSessionTypeId?: string;
  initialAgendaId?: string;
};

const ANY_AGENDA = "";

const MAX_DAYS_AHEAD = 60;

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function PublicBooking({
  slug,
  displayName,
  specialty,
  practiceAddress,
  practicePhone,
  autoConfirm,
  sessionTypes,
  agendaLabel = "Agenda",
  agendas = [],
  initialMonth,
  initialBookableDays,
  initialSessionTypeId,
  initialAgendaId,
}: Props) {
  const [sessionTypeId, setSessionTypeId] = useState(
    sessionTypes.some((t) => t.id === initialSessionTypeId)
      ? initialSessionTypeId!
      : (sessionTypes[0]?.id ?? ""),
  );
  // "" = cualquiera (el server elige la primera libre).
  const [agendaId, setAgendaId] = useState<string>(
    agendas.some((a) => a.id === initialAgendaId) ? initialAgendaId! : ANY_AGENDA,
  );

  // Agendas que ofrecen el servicio elegido. Si queda una sola, no tiene
  // sentido preguntar.
  const agendasForType = useMemo(
    () =>
      agendas.filter(
        (a) =>
          a.sessionTypeIds.length === 0 ||
          a.sessionTypeIds.includes(sessionTypeId),
      ),
    [agendas, sessionTypeId],
  );
  const showAgendaStep = agendasForType.length > 1;

  const [bookableDays, setBookableDays] = useState<Set<string>>(
    () => new Set(initialBookableDays),
  );
  const [daysLoading, setDaysLoading] = useState(false);
  const [monthAnchor, setMonthAnchor] = useState(initialMonth);

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState<string[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slot, setSlot] = useState("");

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [website, setWebsite] = useState(""); // honeypot

  const [pending, startTransition] = useTransition();
  const [state, setState] = useState<BookingFormState>({ status: "idle" });

  const { startMonth, endMonth } = useMemo(() => {
    const today = new Date();
    const end = new Date();
    end.setDate(end.getDate() + MAX_DAYS_AHEAD);
    return { startMonth: today, endMonth: end };
  }, []);

  async function loadBookableDays(
    typeId: string,
    anchor: string,
    agenda: string,
  ) {
    if (!typeId) return;
    setDaysLoading(true);
    try {
      const days = await getBookableDaysAction(
        slug,
        typeId,
        anchor,
        agenda || null,
      );
      setBookableDays(new Set(days));
    } finally {
      setDaysLoading(false);
    }
  }

  async function loadSlots(typeId: string, dateVal: string, agenda: string) {
    if (!typeId || !dateVal) {
      setSlots([]);
      return;
    }
    setSlot("");
    setSlotsLoading(true);
    try {
      const s = await getSlotsAction(slug, typeId, dateVal, agenda || null);
      setSlots(s);
    } finally {
      setSlotsLoading(false);
    }
  }

  function resetDaySelection() {
    setSelectedDate(undefined);
    setDate("");
    setSlots([]);
    setSlot("");
  }

  function onPickSessionType(id: string) {
    setSessionTypeId(id);
    // Cambia la duración → cambia la disponibilidad: reseteamos y recargamos.
    // La agenda elegida puede no ofrecer el servicio nuevo → "cualquiera".
    const stillOffers = agendas.some(
      (a) =>
        a.id === agendaId &&
        (a.sessionTypeIds.length === 0 || a.sessionTypeIds.includes(id)),
    );
    const nextAgenda = stillOffers ? agendaId : ANY_AGENDA;
    setAgendaId(nextAgenda);
    resetDaySelection();
    void loadBookableDays(id, monthAnchor, nextAgenda);
  }

  function onPickAgenda(id: string) {
    setAgendaId(id);
    resetDaySelection();
    void loadBookableDays(sessionTypeId, monthAnchor, id);
  }

  function onMonthChange(month: Date) {
    const anchor = ymd(new Date(month.getFullYear(), month.getMonth(), 1));
    setMonthAnchor(anchor);
    void loadBookableDays(sessionTypeId, anchor, agendaId);
  }

  function onSelectDay(day: Date | undefined) {
    if (!day) return;
    const d = ymd(day);
    setSelectedDate(day);
    setDate(d);
    void loadSlots(sessionTypeId, d, agendaId);
  }

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!slot) return;
    startTransition(async () => {
      const result = await submitBookingAction(
        slug,
        { status: "idle" },
        {
          sessionTypeId,
          agendaId: agendaId || null,
          date,
          startTime: slot,
          contactName: name,
          contactEmail: email,
          contactPhone: phone,
          note,
          website,
        },
      );
      setState(result);
    });
  }

  if (state.status === "confirmed" || state.status === "pending") {
    const ok = state.status === "confirmed";
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-4 py-12">
        <div className="rounded-2xl border bg-card p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Check className="size-6" />
          </div>
          <h1 className="text-xl font-semibold">
            {ok ? "¡Turno confirmado!" : "Solicitud enviada"}
          </h1>
          <p className="text-muted-foreground mt-2 text-sm">{state.message}</p>
          <p className="text-muted-foreground mt-6 text-xs">
            Reservá con Sesionia ·{" "}
            <a href="https://www.sesionia.com" className="text-primary">
              sesionia.com
            </a>
          </p>
        </div>
      </main>
    );
  }

  const calendarClassNames = {
    months: "flex justify-center",
    month: "flex flex-col gap-2",
    month_caption: "relative flex h-9 items-center justify-center",
    caption_label: "text-sm font-medium",
    nav: "absolute inset-x-1 top-0 flex h-9 items-center justify-between",
    button_previous:
      "size-8 inline-flex items-center justify-center rounded-md hover:bg-muted text-foreground/70 hover:text-foreground transition-colors disabled:opacity-30 disabled:pointer-events-none",
    button_next:
      "size-8 inline-flex items-center justify-center rounded-md hover:bg-muted text-foreground/70 hover:text-foreground transition-colors disabled:opacity-30 disabled:pointer-events-none",
  };

  return (
    <main className="mx-auto max-w-4xl px-4 py-10 sm:py-14">
      <div className="grid gap-8 lg:grid-cols-[260px_minmax(0,1fr)] lg:gap-12">
        {/* Datos del profesional */}
        <header className="lg:sticky lg:top-14 lg:self-start">
          <h1 className="text-2xl font-semibold tracking-tight">
            {displayName}
          </h1>
          {specialty && (
            <p className="text-muted-foreground mt-1 text-sm">{specialty}</p>
          )}
          {practiceAddress && (
            <p className="text-muted-foreground mt-3 flex items-start gap-1.5 text-sm">
              <MapPin className="mt-0.5 size-4 shrink-0" />
              {practiceAddress}
            </p>
          )}
          <WhatsappButton
            phone={practicePhone}
            message="Hola, te escribo desde tu página de reservas para hacer una consulta."
            label="Escribinos por WhatsApp"
            className="mt-4"
          />
        </header>

        {/* Widget de reserva */}
        <form onSubmit={onSubmit} className="flex flex-col gap-6">
          {/* Tipo de sesión */}
          {sessionTypes.length > 1 && (
            <div className="grid gap-2">
              <Label>Tipo de turno</Label>
              <div className="flex flex-wrap gap-2">
                {sessionTypes.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => onPickSessionType(t.id)}
                    className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                      sessionTypeId === t.id
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-input hover:bg-accent"
                    }`}
                  >
                    {t.name} <span className="opacity-70">· {t.duration}′</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Sala / persona (multi-agenda): sólo si hay más de una para el servicio */}
          {showAgendaStep && (
            <div className="grid gap-2">
              <Label>{agendaLabel}</Label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => onPickAgenda(ANY_AGENDA)}
                  className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                    agendaId === ANY_AGENDA
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-input hover:bg-accent"
                  }`}
                >
                  Cualquiera
                </button>
                {agendasForType.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => onPickAgenda(a.id)}
                    className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                      agendaId === a.id
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-input hover:bg-accent"
                    }`}
                  >
                    {a.name}
                  </button>
                ))}
              </div>
              <p className="text-muted-foreground text-xs">
                Con &ldquo;Cualquiera&rdquo; te asignamos el primer lugar
                libre.
              </p>
            </div>
          )}

          {/* Calendario + horarios, lado a lado en desktop */}
          <div className="grid gap-6 sm:grid-cols-[auto_minmax(0,1fr)] sm:gap-8">
            <div className="grid content-start gap-2">
              <Label>Elegí un día</Label>
              <div className="relative w-fit rounded-xl border">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={onSelectDay}
                  onMonthChange={onMonthChange}
                  startMonth={startMonth}
                  endMonth={endMonth}
                  disabled={(day) => daysLoading || !bookableDays.has(ymd(day))}
                  showOutsideDays={false}
                  classNames={calendarClassNames}
                />
              </div>
              <p className="text-muted-foreground text-xs">
                {daysLoading
                  ? "Buscando días disponibles…"
                  : "Solo los días con turnos libres."}
              </p>
            </div>

            {/* Horarios */}
            <div className="grid content-start gap-2">
              <Label className="flex items-center gap-1.5">
                <Clock className="size-4" />
                Horario
              </Label>
              {!date ? (
                <p className="text-muted-foreground text-sm">
                  Elegí un día para ver los horarios.
                </p>
              ) : slotsLoading ? (
                <p className="text-muted-foreground flex items-center gap-2 text-sm">
                  <Loader2 className="size-4 animate-spin" />
                  Buscando horarios…
                </p>
              ) : slots.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  No quedan horarios ese día. Probá con otro.
                </p>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {slots.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setSlot(s)}
                      className={`rounded-lg border py-2 text-sm transition-colors ${
                        slot === s
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-input hover:bg-accent"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Datos de contacto (sólo tras elegir slot) */}
          {slot && (
            <div className="flex flex-col gap-4 border-t pt-5">
            <div className="grid gap-1.5">
              <Label htmlFor="name">Nombre y apellido</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={pending}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="phone">Teléfono</Label>
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                disabled={pending}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="email">Email (opcional)</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={pending}
                placeholder="Para recibir la confirmación"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="note">Motivo (opcional)</Label>
              <Textarea
                id="note"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={pending}
              />
            </div>

            {/* Honeypot: oculto para humanos */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              className="absolute left-[-9999px] h-0 w-0 opacity-0"
              aria-hidden="true"
            />

            {state.status === "error" && state.message && (
              <Alert variant="danger">
                <AlertDescription>{state.message}</AlertDescription>
              </Alert>
            )}

            <Button type="submit" disabled={pending} className="w-full">
              {pending ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Check className="size-4" />
              )}
              {autoConfirm ? "Confirmar turno" : "Solicitar turno"}
            </Button>
            {!autoConfirm && (
              <p className="text-muted-foreground text-center text-xs">
                El profesional revisa y confirma tu solicitud.
              </p>
            )}
            </div>
          )}
        </form>
      </div>
    </main>
  );
}
